import { createContext, useContext, useEffect } from 'react';
import { contarDivergenciasDeSaldoPendentes } from '../api/divergencias-saldo.js';
import { useConsulta } from '../api/useConsulta.js';

/**
 * A notificação das divergências de saldo (r1-sync): quantas esperam decisão.
 *
 * O contador mora na moldura (`Layout`), que o mostra como selo na barra
 * lateral, e desce por contexto até a tela das divergências — que o recarrega
 * depois de cada decisão. Sem isso, o selo continuaria dizendo "3" com a tela
 * ao lado mostrando duas, e selo que discorda da tela ensina a ignorar os dois.
 */

/**
 * De quanto em quanto tempo o selo pergunta de novo. A divergência nasce da
 * sincronização com o ERP, sem ninguém do painel fazer nada: sem reconsultar,
 * o selo só mudaria quando a pessoa recarregasse a página. O contador é uma
 * contagem só no servidor, barata o bastante para isto.
 */
export const INTERVALO_DO_CONTADOR_DE_SALDO_MS = 60_000;

export type PendenciasDeSaldo = {
  /** `null` enquanto não se sabe (ou se a consulta falhou): o selo não aparece. */
  readonly pendentes: number | null;
  /** Pergunta de novo agora — depois de uma decisão, por exemplo. */
  readonly recarregar: () => void;
};

const NINGUEM_SABE: PendenciasDeSaldo = {
  pendentes: null,
  recarregar: () => undefined,
};

export const ContextoPendenciasDeSaldo = createContext<PendenciasDeSaldo>(NINGUEM_SABE);

/** O contador vivo, com a reconsulta periódica. Só a moldura chama isto. */
export function useContadorDeDivergenciasDeSaldo(
  intervalo: number = INTERVALO_DO_CONTADOR_DE_SALDO_MS,
): PendenciasDeSaldo {
  const { dados, recarregar } = useConsulta(contarDivergenciasDeSaldoPendentes);

  useEffect(() => {
    const relogio = setInterval(recarregar, intervalo);
    return () => {
      clearInterval(relogio);
    };
  }, [recarregar, intervalo]);

  return { pendentes: dados, recarregar };
}

/**
 * O contador que a moldura fornece. Fora dela (num teste da tela isolada), não
 * sabe de nada — e recarregar não faz nada.
 */
export function usePendenciasDeSaldo(): PendenciasDeSaldo {
  return useContext(ContextoPendenciasDeSaldo);
}
